<script setup>
import MobileSidebar from '@/components/External/MobileSidebar.vue';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { getNotificationsCount, getProfile, logoutUser } from '@/api/userApi.js';
import { useDisplay } from 'vuetify';

const { t, locale } = useI18n();
const { smAndDown, lgAndDown, mdAndDown } = useDisplay();
const route = useRoute();
const router = useRouter();
const store = useStore();
const notificationCount = ref(0);
const mobileMenu = ref(false);
const languages = [
    { title: 'РУС', value: 'ru' },
    { title: 'ҚАЗ', value: 'kk' },
    { title: 'ENG', value: 'en' },
];

const changeLanguage = (lang) => {
    locale.value = lang;
    sessionStorage.setItem('lang', lang);
};

const authorized = computed(() => {
    return store.state.token ? true : false;
});

const fioLink = computed(() => {
    const role = store.state.role;
    if (role === 'USER CC' || role === 'USER DZO' || role === 'USER') return '/my_resume';
    else return '/admin/profile';
});

const FIO = computed(() => {
    const localized = t('general.cabinet');
    return store.state.fio ?? localStorage.getItem('fio') ?? localized;
});

const avatarPath = computed(() => {
    return store.state.avatar ?? localStorage.getItem('avatar') ?? '/images/empty-avatar.svg';
});

const logout = async () => {
    const isImpersonating = sessionStorage.getItem('is_impersonating') === 'true';
    const originalUser = sessionStorage.getItem('original');
    if (isImpersonating && originalUser) {
        store.dispatch('loginAsOriginal');
        window.location.href = '/admin/users';
    }
    else {
        await logoutUser().finally(() => {
            store.dispatch('logout');
            router.push('/login');
        });
    }
};

const getNotifications = async () => {
    if (authorized.value && route.path !== '/login') {
        await getNotificationsCount()
            .then(res => {
                if (res && res.data) notificationCount.value = res.data.count;
            });
    }
};

const storeUserInfo = (info) => {
    const userInfo = {
        is_blocked: info.is_blocked
    };
    store.dispatch('setUserInfo', userInfo);
};

onMounted(async () => {
    if (store.state.user_id && store.state.token) {
        const res = await getProfile(store.state.user_id);
        if (res && res.data) storeUserInfo(res.data);
        
        await getNotifications();
    }
});
</script>

<template>
    <header 
        :class="[
            'w-full py-3 box-border flex justify-between items-center z-[1]',
            smAndDown || mdAndDown ? 'px-3' : lgAndDown ? 'pl-5 pr-10' : 'px-36', 
            route.path !== '/' ? 'bg-[#127CC1]' : 'bg-transparent'
        ]"
    >
        <!-- Левая часть -->
        <div class="flex items-center font-bold">
            <router-link v-if="!smAndDown" to="/" class="mr-10">
                <img 
                    v-if="route.path === '/'"
                    src="/images/kmg_logo.svg" 
                    alt="logo" 
                    class="min-w-64 w-64" 
                />
                <img 
                    v-else
                    src="/images/kmg_logo_white.svg" 
                    alt="logo" 
                    :class="mdAndDown ? 'min-w-48 w-48' : 'min-w-64 w-64'" 
                />
            </router-link>

            <!-- Пункты меню для десктопа -->
            <router-link 
                v-if="!smAndDown"
                to="/vacancies" 
                class="text-white ml-8 mr-5 text-lg"
            >
                {{ $t('general.vacancies') }}
            </router-link>

            <v-menu transition="slide-y-transition">
                <template v-slot:activator="{ props }">
                    <v-btn
                        v-if="!smAndDown"
                        v-bind="props"
                        variant="text"
                        rounded="lg"
                    >
                        <span class="text-none text-white font-bold font-ubuntu tracking-normal text-[17px]">
                            {{ $t('main.info') }}
                        </span>
                    </v-btn>
                </template>
                <v-list>
                    <v-list-item :title="$t('main.contacts')" to="/contacts" />
                    <v-list-item :title="$t('main.about_company')" to="/about" />
                    <v-list-item :title="$t('main.selection_stages')" to="/steps" class="title-uppercase" />
                    <v-list-item :title="$t('general.for_students')" to="/for_students" />
                    <v-list-item :title="$t('main.faq')" to="/faq" />
                </v-list>
            </v-menu>

            <!-- Мобильное бургер-меню -->
            <v-btn 
                v-if="smAndDown" 
                icon="mdi-menu" 
                variant="text"
                class="text-white" 
                @click="mobileMenu = true"
            />
        </div>

        <!-- Правая часть -->
        <div class="flex items-center text-white">
            <v-menu transition="slide-y-transition">
                <template v-slot:activator="{ props }">
                    <v-btn
                        v-if="!smAndDown"
                        v-bind="props"
                        variant="text"
                        rounded="lg"
                        append-icon="mdi-chevron-down"
                        class="font-bold text-base uppercase text-white"
                    >
                        {{  
                            $i18n.locale === 'ru' ? 'РУС' 
                            : $i18n.locale === 'kk' ? 'ҚАЗ' 
                            : 'ENG' 
                        }}
                    </v-btn>
                </template>
                <v-list>
                    <v-list-item
                        v-for="lang of languages" 
                        :key="lang"
                        :title="lang.title" 
                        class="uppercase" 
                        @click="changeLanguage(lang.value)" 
                    />
                </v-list>
            </v-menu>

            <v-btn 
                v-if="authorized"
                variant="text"
                color="#FFFFFF"
                rounded="full"
                width="64"
                height="64"
                :class="smAndDown ? 'mx-1' : 'mx-6'"
                @click="$router.push('/notifications')"
            >
                <v-badge v-if="notificationCount > 0" :content="notificationCount" color="error">
                    <v-icon icon="mdi-bell" size="x-large" />
                </v-badge>
                <v-icon v-else icon="mdi-bell" size="x-large" />
            </v-btn>

            <div v-if="authorized" class="flex flex-col max-w-52 items-end">
                <router-link
                    :to="fioLink"
                    :class="[
                        'font-semibold leading-4 block max-w-[240px] text-right',
                        smAndDown ? 'text-base' : 'text-lg'
                    ]"
                >
                    {{ FIO }}
                </router-link>
                <router-link v-if="!smAndDown" to="/login" class="opacity-[54%]" @click="logout">
                    {{ $t('btns.logout') }}
                </router-link>
            </div>

            <v-avatar
                v-if="authorized"
                :image="avatarPath"
                size="40"
                class="cursor-pointer ml-3"
                @click="$router.push(fioLink)"
            />

            <v-btn 
                v-if="!authorized"
                :text="$t('btns.create_resume')"
                rounded="lg"
                :height="smAndDown ? 34 : 44"
                :color="route.path !== '/' ?  'white' : '#127CC1'"
                :class="['text-none font-normal mx-3', { 'px-2': smAndDown }]"
                :size="smAndDown ? 'default' : 'large'"
                flat
                :variant="route.path !== '/' ? 'outlined' : undefined"
                @click="$router.push('/login')"
            />

            <v-btn 
                v-if="!authorized"
                rounded="lg"
                :height="smAndDown ? 34 : 44"
                :text="$t('btns.to_come_in')"
                color="white"
                :class="['text-none font-normal', { 'px-2': smAndDown }]"
                :size="smAndDown ? 'default' : 'large'"
                flat
                style="color: #127CC1 !important"
                @click="$router.push('/login')"
            />
        </div>
    </header>

    <!-- Мобильное выпадающее меню -->
    <MobileSidebar 
        v-model="mobileMenu"
        @changeLanguage="changeLanguage"
        @logout="logout"
        @close="mobileMenu = false"
    />
</template>

<style>
.title-uppercase .v-list-item-title::first-letter {
    text-transform: uppercase;
}
</style>